import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { User } from 'src/app/core/components/user-admin-page/user-model';
import { lastValueFrom, Subscription } from 'rxjs';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';

@Component({
    selector: 'app-perical-options',
    templateUrl: './perical-options.component.html',
    styles: [``],
    standalone: false
})
export class PericalOptionsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  public verticalGridUnit: string;
  public gridSpacingValidation: { min: number, max: number };
  public successiveRelationFactorValidation: { min: number, max: number } = { min: 0.01, max: 2 };
  public latentHeatDampingFactorValidation: { min: number, max: number } = { min: 0.01, max: 1000 };
  public pericalOptionsForm: UntypedFormGroup;
  public isLoading = signal<boolean>(true);
  public hideGridRefinement: boolean;

  public latentHeatCheckboxInfo = " Phase change and associated latent heat is not present in many operations. However, in some cases it can be significant and should be included. If Latent Heat of Phase Change is included. If Latent Heat of Phase Change is included, it may be necessary to reduce the Relaxation Factor, e.g. 0.1.";
  public latentHeatInputInfo = " The default value of 100 is set to limit numerical instability.  However, it may be reduced to increase the impact of Latent Heat of Phase Change. ";
  public successiveFactorInfo = " The default value of 0.3 is tuned to provide stability but not slow down convergence";

  // State
  @Input()
  private componentId: string;

  public control(name: string): UntypedFormControl {
    return this.pericalOptionsForm.get(name) as UntypedFormControl;
  }

  constructor(
    private _storeService: StoreService,
    private _formBuilder: UntypedFormBuilder,
    private _thermalOperationsService: ThermalOperationsService,
    private _messenger: MediatorService
  ) { }
  
  async ngOnInit(): Promise<void> {
    this.verticalGridUnit = (await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS)).longLengths;
    const user = await this._storeService.get<User>(StorageKeys.USER);
    
    this.gridSpacingValidation = this.verticalGridUnit == 'ft' ? { min: 5, max: 1000 } : { min: 1.5, max: 300 };
    
    const pericalOptions = await lastValueFrom(this._thermalOperationsService.getPericalOptions());
    
    const company = user.profile?.organization?.toLowerCase();
    this.hideGridRefinement = company != 'altus';
    
    this.createReactiveForm();
    this.pericalOptionsForm.patchValue(pericalOptions, { emitEvent: false });

    this.isLoading.set(false);
  }

  private createReactiveForm(): void {
    this.pericalOptionsForm = this._formBuilder.group({
      id: new UntypedFormControl(""),
      maximumGridSpacing: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(this.gridSpacingValidation.min), Validators.max(this.gridSpacingValidation.max)] }),
      disableInterfaceRefinement: new UntypedFormControl(false),
      successiveRelationFactor: new UntypedFormControl({ value: 0.3, disabled: false }, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.01), Validators.max(2)] }),
      latentHeatDampingFactor: new UntypedFormControl({ value: 100, disabled: false }, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.01), Validators.max(1000)] }),
      includeLatentHeatPhaseChange: new UntypedFormControl(false),
      useAdvancedNaturalConvectionCorrelation: new UntypedFormControl(true),
      applyPseudoPhaseEnvelope: new UntypedFormControl(true),
    });

    this._subscriptions.add(this.pericalOptionsForm.valueChanges.subscribe(async () => {
      if (!this.isLoading() && this.pericalOptionsForm.valid) {
        await lastValueFrom(this._thermalOperationsService.setPericalOptions(this.pericalOptionsForm.getRawValue()));
        this._messenger.publish(new DataSavedMessage('PERICAL Options'));
      }
    }));
  }

  ngOnDestroy() {
    this?._subscriptions?.unsubscribe();
  }
}
